import axios from '@/libs/axios'

export default {
  namespaced: true,
  state: {
    marketPrices: [],
    diseaseAlerts: [],
    activeAlertCount: 0,
    myTasks: [],
    pendingTaskCount: 0,
    upcomingVaccinations: [],
    healthScores: {}, // keyed by flockId
    // Company-level module gating for nav visibility. null = not yet fetched
    // (checkPermissions treats non-array as "show everything" — fail open).
    enabledModules: null,
  },
  getters: {
    activeAlertCount: state => state.activeAlertCount,
    pendingTaskCount: state => state.pendingTaskCount,
    healthScore: state => flockId => state.healthScores[flockId],
  },
  mutations: {
    SET_MARKET_PRICES(state, prices) {
      state.marketPrices = prices
    },
    SET_DISEASE_ALERTS(state, alerts) {
      state.diseaseAlerts = alerts
      state.activeAlertCount = alerts.filter(a => !a.is_resolved).length
    },
    SET_TASKS(state, tasks) {
      state.myTasks = tasks
      state.pendingTaskCount = tasks.filter(t => t.status === 'pending').length
    },
    SET_HEALTH_SCORE(state, { flockId, score }) {
      state.healthScores = { ...state.healthScores, [flockId]: score }
    },
    SET_ENABLED_MODULES(state, slugs) {
      state.enabledModules = slugs
    },
  },
  actions: {
    async fetchAlertCount({ commit }) {
      // Check token exists before making call
      const token = sessionStorage.getItem('accessToken') || localStorage.getItem('accessToken')
      if (!token) return  // not logged in, skip silently

      try {
        const { data } = await axios.get('web/disease-alerts', {
          params: { is_resolved: 0, per_page: 100 }
        })
        commit('SET_DISEASE_ALERTS', data.data || [])
      } catch (e) {
        if (e.response?.status === 401) {
          // Token expired — don't show error, axios interceptor handles redirect
          commit('SET_DISEASE_ALERTS', [])
        }
        // For other errors, fail silently (don't spam console in production)
      }
    },
    async fetchPendingTasks({ commit }) {
      const token = sessionStorage.getItem('accessToken') || localStorage.getItem('accessToken')
      if (!token) return
      try {
        const { data } = await axios.get('web/tasks', { params: { status: 'pending', per_page: 100 } })
        commit('SET_TASKS', data.data || [])
      } catch (e) {
        if (e.response?.status !== 401) console.warn('Tasks fetch failed')
      }
    },
    async fetchMarketPrices({ commit }) {
      try {
        const { data } = await axios.get('web/market-prices')
        commit('SET_MARKET_PRICES', data.data || data.items || [])
      } catch (e) {
        // silent fail
      }
    },
    async fetchEnabledModules({ commit }) {
      const token = sessionStorage.getItem('accessToken') || localStorage.getItem('accessToken')
      if (!token) return
      try {
        const { data } = await axios.get('company/enabled-modules')
        const slugs = data?.data?.enabled_modules ?? null
        commit('SET_ENABLED_MODULES', slugs)
        // Keep sessionStorage's userData in sync too, so a full page reload
        // (not just this SPA session) still has the latest value immediately,
        // without waiting for this action to run again.
        try {
          const raw = sessionStorage.getItem('userData') || localStorage.getItem('userData')
          if (raw) {
            const userData = JSON.parse(raw)
            userData.enabledModules = slugs
            sessionStorage.setItem('userData', JSON.stringify(userData))
          }
        } catch (e) {
          // storage sync is best-effort — Vuex state is already the source of truth
        }
      } catch (e) {
        // silent fail — leave enabledModules as whatever it was (fail open on first load)
      }
    },
  },
}
